import {MenuConfig, MenuItemConfig} from '@common/menus/menu-config';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';
import {useSelectedLocale} from '@ui/i18n/selected-locale';
import {useSettings} from '@ui/settings/use-settings';
import {User} from '@ui/types/user';
import dot from 'dot-object';
import {useMemo} from 'react';
import {useAuth} from '../auth/use-auth';

// Rewrite a menu link to the locale-prefixed URL for the active locale.
// We use the item's `type` to know it's a page (not /login, not an external
// link) — admins picking a custom page from the menu editor save the item
// with `type: 'customPage'` (see LoadCustomPageMenuItems::execute). Routes
// that happen to point at a built-in page (e.g. `type: 'route'` with
// action='/pricing') are also rewritten.
//
// Exported so other surfaces that render a configured `MenuItemConfig`
// without going through `useCustomMenu` (e.g. the cookie banner, which
// reads `cookie_notice.button` directly) can apply the same rewrite.
export function localizePageAction(
  action: string,
  type: string,
  localeCode: string,
): string {
  const match = action.match(/^\/?(?:pages\/)?([^/?#]+)$/);
  if (!match) return action;
  const slug = match[1];
  const data = getBootstrapData();

  // built-in pages (contact, pricing): swap to the canonical localized URL
  const builtin = data.builtinPages?.[slug];
  if (builtin) {
    const localized = builtin.translations?.[localeCode] || builtin.slug;
    return `/${localeCode}/${localized}`;
  }

  // custom page (admin picked it from the menu editor) — prefix the active
  // locale unconditionally and, when the page has a translated slug, swap
  // to that locale's slug. We do NOT use a generic single-segment prefix
  // for unknown actions because they might be static routes (/login,
  // /admin, etc.) — only items the admin explicitly tagged as customPage
  // are safe to prefix.
  if (type === 'customPage') {
    const map = data.customPageSlugs?.[slug];
    const newSlug = map?.[localeCode] || slug;
    return `/${localeCode}/${newSlug}`;
  }

  // route to a translated custom page outside the customPage type
  // (legacy menu items where the admin typed `/<slug>` manually)
  const map = data.customPageSlugs?.[slug];
  if (map) {
    return `/${localeCode}/${map[localeCode] || slug}`;
  }

  return action;
}

export function useCustomMenu(menuOrPosition?: string | MenuConfig) {
  const settings = useSettings();
  const {user, hasPermission} = useAuth();
  const {localeCode} = useSelectedLocale();

  return useMemo(() => {
    if (!menuOrPosition) {
      return null;
    }

    const menu =
      typeof menuOrPosition === 'string'
        ? settings.menus?.find(s => s.positions?.includes(menuOrPosition))
        : menuOrPosition;

    if (!menu) {
      return menu ?? null;
    }

    const menuItems: MenuItemConfig[] = [];
    for (const item of menu.items) {
      const hasRoles = (item.roles || []).every(a =>
        user?.roles?.find(b => b.id === a),
      );
      const hasPermissions = (item.permissions || []).every(a =>
        hasPermission(a),
      );
      const hasSettings =
        !item.settings ||
        Object.entries(item.settings).every(([key, value]) => {
          return dot.pick(key, settings) == value;
        });

      if (
        !item.action ||
        !hasRoles ||
        !hasPermissions ||
        !hasSettings ||
        !subscriptionStatusMatches(item, user)
      ) {
        continue;
      }

      // resolve from the original (base-slug) action every render so the href
      // follows the active locale, including on a live language switch
      let action = item.action;
      if (user?.id) {
        action = action.replace(/{currentUser}/g, `${user.id}`);
      }
      action = localizePageAction(action, item.type, localeCode);

      menuItems.push(action === item.action ? item : {...item, action});
    }

    // return a fresh menu object; never mutate the shared settings menus
    return {...menu, items: menuItems};
  }, [hasPermission, settings, menuOrPosition, user, localeCode]);
}

function subscriptionStatusMatches(
  item: MenuItemConfig,
  user?: User | null,
): boolean {
  if (!item.subscriptionStatus) {
    return true;
  }
  const hasActiveSubscription = !!user?.subscriptions?.find(sub => sub.active);
  if (item.subscriptionStatus === 'subscribed') {
    return hasActiveSubscription;
  }
  return !hasActiveSubscription;
}
